import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import type {ReactNode} from 'react';
import {
  EmailAuthProvider,
  getMultiFactorResolver,
  onIdTokenChanged,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut,
  type MultiFactorResolver,
  type User,
} from 'firebase/auth';
import {auth} from './firebase';
import {SessionGate, StaffApi, errorMessage} from './api';
import {runtimeConfig} from './config';
import type {MeAccess} from './types';

interface PendingMfa {
  resolver: MultiFactorResolver;
  verificationId: string;
  phoneLabel: string;
}

interface AuthContextValue {
  user: User | null;
  me: MeAccess | null;
  /** `auth_time` du jeton, en millisecondes : fixe l'heure où la session cesse d'être « récente ». */
  authTimeMs: number | null;
  loading: boolean;
  error: string | null;
  pendingMfa: PendingMfa | null;
  api: StaffApi | null;
  gate: SessionGate;
  dataEpoch: number;
  signIn(email: string, password: string): Promise<void>;
  finishMfa(code: string): Promise<void>;
  reauthenticate(password: string): Promise<void>;
  logOut(): Promise<void>;
  refreshAccess(): Promise<void>;
  clearSessionData(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function firebaseCode(error: unknown): string {
  return typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
}

function authMessage(error: unknown): string {
  const code = firebaseCode(error);
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') return 'Identifiants incorrects.';
  if (code === 'auth/too-many-requests') return 'Trop de tentatives. Réessayez plus tard.';
  if (code === 'auth/code-expired') return 'Le code MFA a expiré.';
  if (code === 'auth/invalid-verification-code') return 'Le code MFA est incorrect.';
  return error instanceof Error ? error.message : "Échec de l'authentification.";
}

export function AuthProvider({children}: {children: ReactNode}) {
  const [user, setUser] = useState<User | null>(null);
  const [me, setMe] = useState<MeAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingMfa, setPendingMfa] = useState<PendingMfa | null>(null);
  const [authTimeMs, setAuthTimeMs] = useState<number | null>(null);
  const [dataEpoch, setDataEpoch] = useState(0);
  const gateRef = useRef(new SessionGate());
  const uidRef = useRef<string | null>(null);
  const api = useMemo(() => user ? new StaffApi(runtimeConfig.apiBase, () => user.getIdToken()) : null, [user]);

  const clearSessionData = useCallback(() => {
    gateRef.current.rotate();
    setMe(null);
    setDataEpoch(value => value + 1);
  }, []);

  const loadAccess = useCallback(async (nextUser: User, nextApi?: StaffApi) => {
    const client = nextApi || new StaffApi(runtimeConfig.apiBase, () => nextUser.getIdToken());
    const snapshot = gateRef.current.snapshot();
    try {
      const access = await client.me(snapshot.signal);
      if (gateRef.current.accepts(snapshot.generation) && auth.currentUser?.uid === nextUser.uid) {
        setMe(access);
        setError(null);
      }
    } catch (caught) {
      if (gateRef.current.accepts(snapshot.generation)) {
        setMe(null);
        setError(errorMessage(caught) || 'Accès impossible.');
      }
    }
  }, []);

  const readAuthTime = useCallback(async (nextUser: User) => {
    try {
      const result = await nextUser.getIdTokenResult();
      const parsed = Date.parse(result.authTime);
      if (auth.currentUser?.uid === nextUser.uid) setAuthTimeMs(Number.isFinite(parsed) ? parsed : null);
    } catch {
      setAuthTimeMs(null);
    }
  }, []);

  useEffect(() => onIdTokenChanged(auth, async nextUser => {
    const changed = uidRef.current !== (nextUser?.uid || null);
    if (changed || !nextUser) clearSessionData();
    uidRef.current = nextUser?.uid || null;
    setUser(nextUser);
    setPendingMfa(null);
    if (nextUser) {
      await readAuthTime(nextUser);
      await loadAccess(nextUser);
    } else setAuthTimeMs(null);
    setLoading(false);
  }), [clearSessionData, loadAccess, readAuthTime]);

  const beginMfa = useCallback(async (caught: unknown) => {
    const resolver = getMultiFactorResolver(auth, caught as Parameters<typeof getMultiFactorResolver>[1]);
    const phoneHint = resolver.hints.find(hint => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
    if (!phoneHint) throw new Error('Ce second facteur nécessite la configuration TOTP de production, encore indisponible dans ce portail.');
    const recaptcha = new RecaptchaVerifier(auth, 'recaptcha-container', {size: 'invisible'});
    try {
      const verificationId = await new PhoneAuthProvider(auth).verifyPhoneNumber({multiFactorHint: phoneHint, session: resolver.session}, recaptcha);
      setPendingMfa({resolver, verificationId, phoneLabel: phoneHint.displayName || 'téléphone enregistré'});
    } finally {
      recaptcha.clear();
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (caught) {
      if (firebaseCode(caught) === 'auth/multi-factor-auth-required') await beginMfa(caught);
      else { setError(authMessage(caught)); throw caught; }
    }
  }, [beginMfa]);

  const finishMfa = useCallback(async (code: string) => {
    if (!pendingMfa) return;
    try {
      const credential = PhoneAuthProvider.credential(pendingMfa.verificationId, code.trim());
      const result = await pendingMfa.resolver.resolveSignIn(PhoneMultiFactorGenerator.assertion(credential));
      await result.user.getIdToken(true);
      setPendingMfa(null);
      clearSessionData();
      await readAuthTime(result.user);
      await loadAccess(result.user);
    } catch (caught) {
      setError(authMessage(caught));
      throw caught;
    }
  }, [clearSessionData, loadAccess, pendingMfa, readAuthTime]);

  const reauthenticate = useCallback(async (password: string) => {
    if (!user?.email) throw new Error('Compte e-mail requis.');
    setError(null);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
      await user.getIdToken(true);
      clearSessionData();
      await readAuthTime(user);
      await loadAccess(user);
    } catch (caught) {
      if (firebaseCode(caught) === 'auth/multi-factor-auth-required') await beginMfa(caught);
      else { setError(authMessage(caught)); throw caught; }
    }
  }, [beginMfa, clearSessionData, loadAccess, readAuthTime, user]);

  const logOut = useCallback(async () => {
    clearSessionData();
    await signOut(auth);
  }, [clearSessionData]);

  const refreshAccess = useCallback(async () => {
    if (!user) return;
    clearSessionData();
    await readAuthTime(user);
    await loadAccess(user);
  }, [clearSessionData, loadAccess, readAuthTime, user]);

  const value = useMemo<AuthContextValue>(() => ({
    user, me, authTimeMs, loading, error, pendingMfa, api, gate: gateRef.current, dataEpoch,
    signIn, finishMfa, reauthenticate, logOut, refreshAccess, clearSessionData,
  }), [api, authTimeMs, clearSessionData, dataEpoch, error, finishMfa, loading, logOut, me, pendingMfa, reauthenticate, refreshAccess, signIn, user]);

  return <AuthContext.Provider value={value}>{children}<div id="recaptcha-container" /></AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('AuthProvider manquant');
  return context;
}
